/* LandingSections.jsx — the marketing landing page sections, ported from the
   "JAI Onboarding" design prototype. Composition matches the prototype's
   LandingStage: TopNav → Hero → Spotlight → Arsenal → HowScreens → About →
   Finale → Footer.

   All sections take two callbacks from the page:
     onNav(id)   — smooth-scroll to a section id ('features' | 'how' | 'about')
     onAuth(mode)— route to real auth ('login' | 'signup') */

import { useState, useEffect } from 'react'
import { JaiMark } from '../JaiBrand'
import { JAIMascot } from '../JAIMascot'
import {
  JAI, FONTS, L_TOOLS, L_TOOL_COUNT, L_TOOL_COUNT_WORD,
  HOW_STEPS, ABOUT_PRINCIPLES, ToolGlyph, LArrow, LCheck, Reveal, AppStoreButton,
} from './landingShared'
import { IS_NATIVE_APP } from '../../services/runtime'

// This whole marketing page is replaced by a compact native welcome screen
// on iOS (see pages/Landing.jsx), so in practice these nav bars don't render
// there at all — but the Pricing link is dropped here too, defensively, per
// Guideline 3.1.1: nothing that points at buying anything should exist in
// code paths reachable from native, even ones that are currently unreachable
// for an unrelated reason.
const NAV_LINKS = IS_NATIVE_APP
  ? [['Features', 'features'], ['How JAI Works', 'how'], ['About us', 'about']]
  : [['Features', 'features'], ['How JAI Works', 'how'], ['Pricing', 'pricing'], ['About us', 'about']]

/* ── sticky top nav (links + auth reveal on scroll) ──────────────────────── */
export function TopNav({ onNav, onAuth }) {
  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 60)
    window.addEventListener('scroll', fn, { passive: true })
    fn()
    return () => window.removeEventListener('scroll', fn)
  }, [])
  const links = NAV_LINKS
  const linkBtn = { font: 'inherit', fontFamily: FONTS.tight, fontSize: 14, fontWeight: 650, color: 'rgba(255,255,255,0.7)', background: 'none', border: 'none', borderRadius: 999, padding: '8px 13px', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'color 0.2s, background 0.2s' }
  return (
    <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '13px 26px', background: scrolled ? 'rgba(6,16,40,0.82)' : 'transparent', backdropFilter: scrolled ? 'blur(16px)' : 'none', borderBottom: scrolled ? '1px solid rgba(255,255,255,0.08)' : '1px solid transparent', boxShadow: scrolled ? '0 14px 44px rgba(0,4,16,0.45)' : 'none', transition: 'background 0.35s, border-color 0.35s, box-shadow 0.35s' }}>
      <button type="button" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
        <JaiMark size={30} bg={JAI.blue} accent="#7AB8FF"></JaiMark>
        <span style={{ fontSize: 19, fontWeight: 800, letterSpacing: '-0.04em', color: 'white', fontFamily: FONTS.tight }}>JAI</span>
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, opacity: scrolled ? 1 : 0, transform: scrolled ? 'none' : 'translateY(-6px)', pointerEvents: scrolled ? 'auto' : 'none', transition: 'opacity 0.3s, transform 0.3s' }}>
        <div className="v2-navlinks" style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {links.map(([l, id]) => (
            <button key={id} type="button" style={linkBtn} onClick={() => onNav(id)}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'white'; e.currentTarget.style.background = 'rgba(255,255,255,0.07)' }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; e.currentTarget.style.background = 'none' }}>{l}</button>
          ))}
        </div>
        <span style={{ width: 1, height: 22, background: 'rgba(255,255,255,0.14)' }}></span>
        <button type="button" onClick={() => onAuth('login')} style={linkBtn}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'white'; e.currentTarget.style.background = 'rgba(255,255,255,0.07)' }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; e.currentTarget.style.background = 'none' }}>Sign in</button>
        <button type="button" onClick={() => onAuth('signup')} className="v2-cta" style={{ font: 'inherit', fontSize: 14, fontWeight: 760, color: '#04122E', border: 'none', cursor: 'pointer', background: 'white', borderRadius: 999, padding: '11px 22px', fontFamily: FONTS.tight }}>Get started</button>
      </div>
    </nav>
  )
}

/* ── HERO ────────────────────────────────────────────────────────────────── */
const H_QA = [
  { q: 'Should I take this trade?', a: 'No. You’d lose the deal by 11 points a week. Counter already drafted.', stamp: 'C-', sc: '#FF5C5C', tag: 'TRADE ANALYSER' },
  { q: 'Best pickup tonight?', a: 'M. Carter — 94 fit for your roster. Claim before your rival wakes up.', stamp: '94', sc: 'var(--accent)', tag: 'WAIVER WIRE' },
  { q: 'Can I win it all?', a: '18% and climbing. Win out, hold priority — I’ve mapped the path.', stamp: '18%', sc: '#4ADE94', tag: '10,000 SIMS' },
]

function HeroComposer() {
  const [idx, setIdx] = useState(0)
  const [chars, setChars] = useState(0)
  const [phase, setPhase] = useState('typing')
  const qa = H_QA[idx]

  useEffect(() => {
    let t
    if (phase === 'typing') {
      if (chars < qa.q.length) t = setTimeout(() => setChars(chars + 1), 45)
      else t = setTimeout(() => setPhase('thinking'), 380)
    } else if (phase === 'thinking') {
      t = setTimeout(() => setPhase('answer'), 1000)
    } else {
      t = setTimeout(() => { setPhase('typing'); setChars(0); setIdx((idx + 1) % H_QA.length) }, 4400)
    }
    return () => clearTimeout(t)
  }, [phase, chars, idx])

  const mascotState = phase === 'typing' ? 'idle' : phase === 'thinking' ? 'thinking' : 'smug'

  return (
    <div style={{ width: 640, maxWidth: '92vw', position: 'relative', textAlign: 'left' }}>
      {/* mascot perched on the composer */}
      <div className="v2-float" style={{ position: 'absolute', right: 34, top: -86, zIndex: 2 }}>
        <JAIMascot size={74} state={mascotState}></JAIMascot>
      </div>

      <div style={{ background: 'rgba(10,24,56,0.55)', border: '1px solid rgba(255,255,255,0.13)', borderRadius: 18, padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 15, boxShadow: '0 30px 90px rgba(0,4,16,0.7), 0 0 50px color-mix(in oklab, var(--accent) 14%, transparent)', backdropFilter: 'blur(10px)', position: 'relative' }}>
        <span style={{ fontFamily: FONTS.mono, fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', color: 'rgba(255,255,255,0.38)', flexShrink: 0 }}>ASK JAI</span>
        <div style={{ flex: 1, fontSize: 18, fontWeight: 650, color: 'white', minHeight: 26, letterSpacing: '-0.01em' }}>
          {qa.q.slice(0, phase === 'typing' ? chars : qa.q.length)}
          <span className="c-caret" style={{ display: 'inline-block', width: 2, height: 20, background: 'var(--accent)', marginLeft: 3, verticalAlign: 'text-bottom' }}></span>
        </div>
        <div style={{ width: 40, height: 40, borderRadius: 12, background: phase !== 'typing' ? 'var(--accent)' : 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', flexShrink: 0, transition: 'background 0.3s, box-shadow 0.3s', boxShadow: phase !== 'typing' ? '0 0 24px color-mix(in oklab, var(--accent) 55%, transparent)' : 'none' }}>
          <svg width="17" height="17" viewBox="0 0 16 16" fill="none"><path d="M8 13V3M3.5 7.5 8 3l4.5 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"></path></svg>
        </div>
      </div>

      <div style={{ height: 118, marginTop: 14, position: 'relative' }}>
        {phase === 'answer' &&
          <div className="a-row-in" style={{ background: 'rgba(6,16,40,0.85)', border: '1px solid color-mix(in oklab, var(--accent) 30%, transparent)', borderRadius: 16, padding: '17px 21px', display: 'flex', alignItems: 'center', gap: 18, boxShadow: '0 24px 60px rgba(0,4,16,0.6)' }}>
            <span className="v2-stamp" style={{ fontFamily: FONTS.mono, fontSize: 26, fontWeight: 800, color: qa.sc, border: `2px solid ${qa.sc}`, borderRadius: 10, padding: '8px 13px', flexShrink: 0, letterSpacing: '-0.02em' }}>{qa.stamp}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: FONTS.mono, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.16em', color: 'var(--accent)', marginBottom: 6 }}>{qa.tag}</div>
              <p style={{ fontSize: 15.5, lineHeight: 1.5, color: 'rgba(255,255,255,0.9)', margin: 0, textWrap: 'pretty', fontWeight: 550 }}>{qa.a}</p>
            </div>
          </div>
        }
        {phase === 'thinking' &&
          <div className="a-row-in" style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '14px 6px' }}>
            {[0, 1, 2].map((i) => <span key={i} className="v2-dot" style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)', display: 'inline-block', animationDelay: `${i * 0.18}s` }}></span>)}
            <span style={{ fontFamily: FONTS.mono, fontSize: 10.5, letterSpacing: '0.15em', color: 'rgba(255,255,255,0.42)' }}>RUNNING 10,000 SIMULATIONS</span>
          </div>
        }
      </div>
    </div>
  )
}

function HeroNav({ onNav, onAuth }) {
  const links = NAV_LINKS
  return (
    <div className="hero-nav" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '8px 8px 8px 12px', borderRadius: 999, background: 'rgba(10,24,56,0.5)', border: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(12px)', boxShadow: '0 14px 44px rgba(0,4,16,0.4)' }}>
      <div className="hero-nav-links">
        {links.map(([l, id]) =>
          <button key={l} type="button" onClick={() => onNav(id)}
            style={{ font: 'inherit', fontFamily: FONTS.tight, fontSize: 14, fontWeight: 650, color: 'rgba(255,255,255,0.66)', background: 'none', border: 'none', borderRadius: 999, padding: '9px 14px', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'color 0.2s, background 0.2s' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'white'; e.currentTarget.style.background = 'rgba(255,255,255,0.07)' }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.66)'; e.currentTarget.style.background = 'none' }}>{l}</button>
        )}
      </div>
      <span className="hero-nav-div" style={{ width: 1, height: 24, background: 'rgba(255,255,255,0.14)', flexShrink: 0 }}></span>
      <button type="button" onClick={() => onAuth('login')}
        style={{ font: 'inherit', fontFamily: FONTS.tight, fontSize: 14, fontWeight: 650, color: 'rgba(255,255,255,0.66)', background: 'none', border: 'none', borderRadius: 999, padding: '9px 14px', cursor: 'pointer', whiteSpace: 'nowrap' }}
        onMouseEnter={(e) => { e.currentTarget.style.color = 'white' }}
        onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.66)' }}>Sign in</button>
      <button type="button" onClick={() => onAuth('signup')} className="v2-cta" style={{ font: 'inherit', fontSize: 14, fontWeight: 760, color: '#04122E', border: 'none', cursor: 'pointer', background: 'white', borderRadius: 999, padding: '11px 22px', whiteSpace: 'nowrap', fontFamily: FONTS.tight }}>Get started</button>
    </div>
  )
}

export function V2Hero({ onNav, onAuth }) {
  const [booted, setBooted] = useState(false)
  useEffect(() => { const t = setTimeout(() => setBooted(true), 60); return () => clearTimeout(t) }, [])
  const slam = 'v2-slam' + (booted ? ' in' : '')
  return (
    <header style={{ minHeight: 'calc(100vh / var(--jai-zoom))', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', position: 'relative', padding: '92px 40px 70px', overflow: 'hidden' }}>
      {/* atmosphere */}
      <div className="v2-drift" style={{ position: 'absolute', left: '50%', top: '-22%', width: 1100, height: 800, transform: 'translateX(-50%)', background: 'radial-gradient(ellipse, color-mix(in oklab, var(--accent) 13%, transparent), transparent 62%)', pointerEvents: 'none' }}></div>
      <div style={{ position: 'absolute', left: '-12%', bottom: '-30%', width: 700, height: 700, background: 'radial-gradient(circle, rgba(30,91,214,0.1), transparent 60%)', pointerEvents: 'none' }}></div>
      <svg className="a-spin" width="1400" height="1400" viewBox="0 0 1400 1400" fill="none" style={{ position: 'absolute', left: '50%', top: '50%', marginLeft: -700, marginTop: -700, opacity: 0.35, pointerEvents: 'none' }}>
        <circle cx="700" cy="700" r="560" stroke="rgba(122,184,255,0.07)" strokeWidth="1"></circle>
        <circle cx="700" cy="700" r="430" stroke="rgba(122,184,255,0.05)" strokeWidth="1"></circle>
        <path d="M 700 140 A 560 560 0 0 1 1185 420" stroke="color-mix(in oklab, var(--accent) 45%, transparent)" strokeWidth="2.5" strokeLinecap="round"></path>
        <circle cx="1185" cy="420" r="5" fill="var(--accent)"></circle>
      </svg>

      <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div className={slam} style={{ marginBottom: 34 }}>
          <HeroNav onNav={onNav} onAuth={onAuth}></HeroNav>
        </div>
        <div className={slam} style={{ fontFamily: FONTS.mono, fontSize: 11.5, fontWeight: 700, letterSpacing: '0.22em', color: 'var(--accent)', marginBottom: 26, transitionDelay: '0.06s' }}>
          YOUR AI CO-MANAGER · ALWAYS ON
        </div>
        <h1 style={{ margin: 0, fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase', letterSpacing: '-0.072em', lineHeight: 0.9, fontSize: 'clamp(56px, 8.3vw, 120px)', color: 'white' }}>
          <span className={slam} style={{ display: 'block', transitionDelay: '0.12s', fontFamily: "Garamond, Georgia, 'Times New Roman', serif" }}>Stop guessing.</span>
          <span className={slam + ' v2-outline'} style={{ display: 'block', transitionDelay: '0.24s', fontFamily: "Helvetica, 'Helvetica Neue', Arial, sans-serif", letterSpacing: '0em' }}>Start winning.</span>
        </h1>
        <p className={slam} style={{ transitionDelay: '0.38s', fontSize: 18.5, lineHeight: 1.55, color: 'rgba(255,255,255,0.58)', maxWidth: 520, margin: '30px 0 0', textWrap: 'pretty', fontWeight: 500 }}>
          JAI watches every game, grades every trade and finds every edge in your fantasy league — then tells you the move.
        </p>
        <div className={slam} style={{ transitionDelay: '0.5s', marginTop: 72 }}>
          <HeroComposer></HeroComposer>
        </div>
      </div>

      {/* scroll cue */}
      <div style={{ position: 'absolute', bottom: 26, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
        <span style={{ fontFamily: FONTS.mono, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.2em', color: 'rgba(255,255,255,0.32)' }}>JAI IS ALREADY WORKING</span>
        <span className="v2-scrollline" style={{ width: 1.5, height: 34, background: 'linear-gradient(to bottom, var(--accent), transparent)', display: 'block' }}></span>
      </div>
    </header>
  )
}

/* ── SPOTLIGHT ("The edge", light band) ──────────────────────────────────── */
const SP = {
  paper: '#F4F7FB', card: '#FFFFFF', ink: '#0A1F44',
  mute: 'rgba(10,31,68,0.6)', faint: 'rgba(10,31,68,0.42)',
  hair: 'rgba(10,31,68,0.1)', hairSoft: 'rgba(10,31,68,0.06)',
}

function SpotCard({ children, delay }) {
  return (
    <Reveal delay={delay} className="sp-card" style={{ background: SP.card, border: `1px solid ${SP.hair}`, borderRadius: 18, padding: '28px 28px 30px', display: 'flex', flexDirection: 'column', boxShadow: '0 1px 0 rgba(10,31,68,0.04)' }}>
      {children}
    </Reveal>
  )
}

function SpotHead({ children }) {
  return <div style={{ fontFamily: FONTS.mono, fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', color: SP.faint, marginBottom: 22 }}>{children}</div>
}

function SpotBar({ name, fit, top }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 0', borderTop: `1px solid ${SP.hairSoft}` }}>
      <span style={{ flex: '0 0 86px', fontSize: 14, fontWeight: 700, color: top ? SP.ink : SP.mute }}>{name}</span>
      {top && <span style={{ fontSize: 8, fontWeight: 800, letterSpacing: '0.08em', background: 'var(--accent)', color: 'white', borderRadius: 4, padding: '2px 6px', flexShrink: 0 }}>PICK</span>}
      <div style={{ flex: 1, height: 5, borderRadius: 999, background: 'rgba(10,31,68,0.08)', overflow: 'hidden' }}>
        <div style={{ width: `${fit}%`, height: '100%', borderRadius: 999, background: top ? 'var(--accent)' : 'rgba(10,31,68,0.22)' }}></div>
      </div>
      <span style={{ fontFamily: FONTS.mono, fontSize: 12.5, fontWeight: 700, color: top ? 'var(--accent)' : SP.faint, width: 22, textAlign: 'right' }}>{fit}</span>
    </div>
  )
}

export function V2Spotlight() {
  return (
    <section id="features" style={{ background: SP.paper, color: SP.ink, padding: '120px 40px 130px', scrollMarginTop: 70, position: 'relative' }}>
      <div style={{ maxWidth: 1120, margin: '0 auto' }}>
        <Reveal style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 76px' }}>
          <div style={{ fontFamily: FONTS.mono, fontSize: 11, fontWeight: 700, letterSpacing: '0.2em', color: 'var(--accent)', marginBottom: 18 }}>THE EDGE</div>
          <h2 style={{ fontSize: 'clamp(40px, 5.4vw, 62px)', fontWeight: 880, fontStyle: 'italic', textTransform: 'uppercase', letterSpacing: '-0.05em', margin: 0, lineHeight: 0.94, color: SP.ink }}>
            Every call.<br /><span style={{ color: SP.faint }}>Every week.</span>
          </h2>
          <p style={{ fontSize: 17, lineHeight: 1.55, color: SP.mute, margin: '24px auto 0', maxWidth: 480, textWrap: 'pretty' }}>
            One place to ask, {L_TOOL_COUNT_WORD.toLowerCase()} tools behind every answer — no dashboards to dig through. Whatever the decision, JAI hands you the move, the reason, and the receipts.
          </p>
        </Reveal>

        <Reveal style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 22 }}>
          <div style={{ fontFamily: FONTS.mono, fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', color: SP.faint, whiteSpace: 'nowrap' }}>THREE ANSWERS THIS WEEK</div>
          <div style={{ flex: 1, height: 1, background: SP.hair }}></div>
        </Reveal>
        <div className="sp-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 22 }}>
          <SpotCard delay={0}>
            <SpotHead>TRADE ANALYSER · GRADED 1.2s</SpotHead>
            <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: 20 }}>
              <span style={{ fontFamily: FONTS.mono, fontSize: 30, fontWeight: 800, color: '#E0463B', border: '2.5px solid #E0463B', borderRadius: 11, padding: '8px 14px', transform: 'rotate(-4deg)', flexShrink: 0 }}>C-</span>
              <div>
                <div style={{ fontSize: 21, fontWeight: 850, fontStyle: 'italic', letterSpacing: '-0.02em', lineHeight: 1, color: SP.ink }}>Robbery.<br />Decline.</div>
              </div>
            </div>
            <p style={{ fontSize: 14.5, lineHeight: 1.55, color: SP.mute, margin: 'auto 0 0', textWrap: 'pretty' }}>
              You'd bleed <b style={{ color: SP.ink }}>11.3 pts</b> a week. The counter that wins the deal is already drafted.
            </p>
          </SpotCard>

          <SpotCard delay={90}>
            <SpotHead>WAIVER WIRE · 412 SCANNED</SpotHead>
            <p style={{ fontSize: 14.5, lineHeight: 1.5, color: SP.mute, margin: '0 0 8px', fontWeight: 550 }}>Three names tonight. The first won't last till morning.</p>
            <div style={{ marginTop: 'auto' }}>
              <SpotBar name="M. Carter" fit={94} top></SpotBar>
              <SpotBar name="J. Okafor" fit={81}></SpotBar>
              <SpotBar name="T. Brooks" fit={74}></SpotBar>
            </div>
          </SpotCard>

          <SpotCard delay={180}>
            <SpotHead>ASK JAI · TITLE ODDS</SpotHead>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, marginBottom: 6 }}>
              <div style={{ fontSize: 62, fontWeight: 900, fontStyle: 'italic', letterSpacing: '-0.05em', color: SP.ink, lineHeight: 0.85 }}>
                18<span style={{ fontSize: 30, color: 'var(--accent)' }}>%</span>
              </div>
              <div style={{ fontFamily: FONTS.mono, fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: '#1A8754', paddingBottom: 9 }}>▲ 3 THIS WEEK</div>
            </div>
            <svg width="100%" height="46" viewBox="0 0 320 46" preserveAspectRatio="none" style={{ margin: '12px 0 2px' }}>
              <polyline points="0,40 45,35 90,37 135,25 180,28 225,17 270,19 320,8"
                fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"></polyline>
            </svg>
            <p style={{ fontSize: 14, lineHeight: 1.5, color: SP.mute, margin: 'auto 0 0', textWrap: 'pretty' }}>Ask JAI re-runs it after every game — your real path to the title, kept current.</p>
          </SpotCard>
        </div>
      </div>
    </section>
  )
}

/* ── ARSENAL (dark band, Ask JAI feature + tool grid) ────────────────────── */
function ToolTile({ t, i }) {
  return (
    <Reveal delay={(i % 3) * 70} className="v2-tool" style={{ display: 'flex', gap: 14, alignItems: 'flex-start', padding: '17px 18px', borderRadius: 14, background: 'rgba(10,24,56,0.42)', border: '1px solid rgba(255,255,255,0.08)' }}>
      <span style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 1, display: 'flex' }}><ToolGlyph id={t.id} size={22}></ToolGlyph></span>
      <div>
        <div style={{ fontSize: 16, fontWeight: 780, color: 'white', letterSpacing: '-0.012em' }}>{t.name}</div>
        <p style={{ fontSize: 13, lineHeight: 1.45, color: 'rgba(255,255,255,0.55)', margin: '5px 0 0', textWrap: 'pretty' }}>{t.desc}</p>
      </div>
    </Reveal>
  )
}

function AskJaiFeature() {
  return (
    <Reveal style={{ marginBottom: 46 }}>
      <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 22, border: '1px solid color-mix(in oklab, var(--accent) 34%, transparent)', background: 'linear-gradient(150deg, color-mix(in oklab, var(--accent) 18%, rgba(6,16,40,0.6)), rgba(6,16,40,0.6) 62%)', boxShadow: '0 30px 80px rgba(0,4,16,0.5)' }}>
        <div aria-hidden="true" style={{ position: 'absolute', right: '-6%', top: '-45%', width: 420, height: 420, background: 'radial-gradient(circle, color-mix(in oklab, var(--accent) 32%, transparent), transparent 62%)', pointerEvents: 'none' }}></div>
        <div className="v2-feature-row" style={{ position: 'relative', display: 'grid', gridTemplateColumns: '1.05fr 0.95fr', gap: 34, alignItems: 'center', padding: '34px 36px' }}>
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 999, padding: '5px 13px 5px 9px', marginBottom: 20 }}>
              <span style={{ color: 'white', display: 'flex' }}><ToolGlyph id="ask" size={15}></ToolGlyph></span>
              <span style={{ fontFamily: FONTS.mono, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.16em', color: 'rgba(255,255,255,0.82)' }}>THE HEADLINE FEATURE</span>
            </div>
            <h3 style={{ fontSize: 'clamp(30px, 3.7vw, 44px)', fontWeight: 880, fontStyle: 'italic', textTransform: 'uppercase', letterSpacing: '-0.045em', margin: 0, lineHeight: 0.94, color: 'white' }}>
              Just ask JAI.
            </h3>
            <p style={{ fontSize: 15.5, lineHeight: 1.6, color: 'rgba(255,255,255,0.74)', margin: '16px 0 0', maxWidth: 430, textWrap: 'pretty' }}>
              The one feature you actually talk to. Ask anything — start/sit, trade, waiver, draft — and it reaches for whichever of the {L_TOOL_COUNT_WORD.toLowerCase()} tools the question needs, then hands you the move and the reason. Every other tool feeds it.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'rgba(6,16,40,0.7)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 14, padding: '13px 15px' }}>
              <span style={{ fontFamily: FONTS.mono, fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.4)', flexShrink: 0 }}>ASK JAI</span>
              <span style={{ flex: 1, fontSize: 14.5, fontWeight: 600, color: 'white' }}>Start Mobley or Sabonis this week?</span>
              <span style={{ width: 30, height: 30, borderRadius: 9, background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 0 20px color-mix(in oklab, var(--accent) 55%, transparent)' }}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 13V3M3.5 7.5 8 3l4.5 4.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"></path></svg>
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, background: 'rgba(6,16,40,0.85)', border: '1px solid color-mix(in oklab, var(--accent) 30%, transparent)', borderRadius: 14, padding: '14px 16px' }}>
              <span style={{ fontFamily: FONTS.mono, fontSize: 15, fontWeight: 800, color: '#4ADE94', border: '2px solid #4ADE94', borderRadius: 9, padding: '6px 11px', flexShrink: 0 }}>START</span>
              <div>
                <div style={{ fontFamily: FONTS.mono, fontSize: 8.5, fontWeight: 700, letterSpacing: '0.13em', color: 'var(--accent)', marginBottom: 4 }}>SCHEDULE SURGEON · PLAYER ANALYSER</div>
                <p style={{ fontSize: 13.5, lineHeight: 1.5, color: 'rgba(255,255,255,0.9)', margin: 0, textWrap: 'pretty' }}><b style={{ color: 'white' }}>Mobley, +6.4 pts.</b> He's got the back-to-back and the softer matchup.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Reveal>
  )
}

export function V2Arsenal() {
  return (
    <section id="arsenal" style={{ position: 'relative', overflow: 'hidden', padding: '120px 40px 128px', scrollMarginTop: 70 }}>
      <div aria-hidden="true" style={{ position: 'absolute', left: '-10%', top: '4%', width: 680, height: 680, background: 'radial-gradient(circle, color-mix(in oklab, var(--accent) 12%, transparent), transparent 62%)', pointerEvents: 'none' }}></div>
      <div style={{ maxWidth: 1120, margin: '0 auto', position: 'relative' }}>
        <Reveal style={{ maxWidth: 780, marginBottom: 42 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 11, fontWeight: 700, letterSpacing: '0.2em', color: 'var(--accent)' }}>THE ARSENAL</div>
            <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.12)' }}></div>
          </div>
          <h2 style={{ fontSize: 'clamp(40px, 5.4vw, 60px)', fontWeight: 880, fontStyle: 'italic', textTransform: 'uppercase', letterSpacing: '-0.05em', margin: 0, lineHeight: 0.94, color: 'white' }}>
            One place to ask.<br /><span style={{ color: 'rgba(255,255,255,0.35)' }}>{L_TOOL_COUNT_WORD} tools behind it.</span>
          </h2>
          <p style={{ fontSize: 16.5, lineHeight: 1.55, color: 'rgba(255,255,255,0.6)', margin: '22px 0 0', maxWidth: 560, textWrap: 'pretty' }}>
            Ask JAI is the front door. Underneath it, {L_TOOL_COUNT_WORD.toLowerCase()} specialist tools share everything they learn about your league — so no answer is ever made in isolation.
          </p>
        </Reveal>

        <AskJaiFeature></AskJaiFeature>

        <Reveal style={{ display: 'flex', alignItems: 'baseline', gap: 16, marginBottom: 18, borderTop: '1px solid rgba(255,255,255,0.12)', paddingTop: 22 }}>
          <span style={{ fontSize: 23, fontWeight: 850, fontStyle: 'italic', textTransform: 'uppercase', letterSpacing: '-0.035em', color: 'white' }}>The tools behind it</span>
          <span style={{ fontFamily: FONTS.mono, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.4)' }}>{L_TOOL_COUNT} TOOLS</span>
        </Reveal>
        <div className="v2-arsenal-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
          {L_TOOLS.map((t, i) => <ToolTile key={t.id} t={t} i={i}></ToolTile>)}
        </div>
      </div>
    </section>
  )
}

/* ── HOW JAI WORKS (light band, 3-step timeline) ─────────────────────────── */
export function V2HowScreens() {
  const C = { ink: JAI.ink, mute: JAI.mute, faint: JAI.faint, hair: JAI.hair, accent: JAI.blue }
  return (
    <section id="how" style={{ background: '#F4F7FB', color: C.ink, padding: '120px 40px 130px', scrollMarginTop: 70 }}>
      <div style={{ maxWidth: 1080, margin: '0 auto' }}>
        <Reveal style={{ maxWidth: 640, margin: '0 auto 84px', textAlign: 'center' }}>
          <div style={{ fontFamily: FONTS.mono, fontSize: 11, fontWeight: 700, letterSpacing: '0.2em', color: C.accent, marginBottom: 16 }}>HOW JAI WORKS</div>
          <h2 style={{ fontSize: 'clamp(38px, 5.2vw, 58px)', fontWeight: 880, fontStyle: 'italic', textTransform: 'uppercase', letterSpacing: '-0.05em', margin: 0, lineHeight: 0.96, color: C.ink }}>
            Live in a minute.<br /><span style={{ color: C.faint }}>Winning by tonight.</span>
          </h2>
          <p style={{ fontSize: 17, lineHeight: 1.55, color: C.mute, margin: '22px auto 0', maxWidth: 500, textWrap: 'pretty' }}>
            From sign-up to your first graded call is three steps — and JAI does the heavy lifting in every one.
          </p>
        </Reveal>

        <div style={{ maxWidth: 600, margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {HOW_STEPS.map((s, i) => (
            <div key={s.n} style={{ display: 'contents' }}>
              {i > 0 && <div aria-hidden="true" style={{ width: 2, height: 56, background: `linear-gradient(${C.hair}, ${C.accent})`, opacity: 0.55, margin: '10px 0' }}></div>}
              <Reveal delay={i * 60} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <div style={{ width: 64, height: 64, borderRadius: 999, border: `2px solid ${C.accent}`, background: '#F4F7FB', color: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONTS.mono, fontSize: 19, fontWeight: 800, letterSpacing: '0.02em' }}>{s.n}</div>
                <h3 style={{ fontSize: 'clamp(26px, 3.4vw, 36px)', fontWeight: 850, fontStyle: 'italic', letterSpacing: '-0.04em', lineHeight: 1, margin: '22px 0 0', color: C.ink, textTransform: 'uppercase' }}>{s.t}</h3>
                <p style={{ fontSize: 16.5, lineHeight: 1.62, color: C.mute, margin: '15px 0 0', maxWidth: 460, textWrap: 'pretty' }}>{s.d}</p>
              </Reveal>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ── ABOUT US (dark band, story) ─────────────────────────────────────────── */
export function V2About() {
  return (
    <section id="about" style={{ position: 'relative', padding: '150px 40px 40px', scrollMarginTop: 70, overflow: 'hidden' }}>
      <div style={{ position: 'absolute', right: '-10%', top: '6%', width: 620, height: 620, background: 'radial-gradient(circle, color-mix(in oklab, var(--accent) 12%, transparent), transparent 62%)', pointerEvents: 'none' }}></div>
      <div style={{ maxWidth: 1080, margin: '0 auto', position: 'relative' }}>
        <div className="v2-how-grid" style={{ display: 'grid', gridTemplateColumns: '1.1fr 0.9fr', gap: 64, alignItems: 'start' }}>
          <Reveal>
            <div style={{ fontFamily: FONTS.mono, fontSize: 11, fontWeight: 700, letterSpacing: '0.2em', color: 'var(--accent)', marginBottom: 16 }}>ABOUT US</div>
            <h2 style={{ fontSize: 'clamp(38px, 5.2vw, 58px)', fontWeight: 880, fontStyle: 'italic', textTransform: 'uppercase', letterSpacing: '-0.05em', margin: 0, lineHeight: 0.96, color: 'white' }}>
              Luck is not<br /><span style={{ color: 'rgba(255,255,255,0.35)' }}>a strategy.</span>
            </h2>
            <div style={{ marginTop: 28, display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 500 }}>
              <p style={{ fontSize: 16.5, lineHeight: 1.65, color: 'rgba(255,255,255,0.72)', margin: 0, textWrap: 'pretty' }}>
                JAI is an AI-powered fantasy sports co-manager built on a simple belief: your fantasy edge shouldn't cost you hours of research every week. Connect your NFL or NBA leagues and get instant, AI-driven rankings, matchup insights, and start/sit recommendations — tailored to your team, your league, and your scoring settings. No more tab-hopping between five sites or scrolling stat sheets at midnight before your lineup locks.
              </p>
              <p style={{ fontSize: 16.5, lineHeight: 1.65, color: 'white', margin: 0, textWrap: 'pretty', fontWeight: 560 }}>
                That's JAI — one place to ask, Ask JAI, with {L_TOOL_COUNT_WORD.toLowerCase()} tools sharing one brain underneath it, working your league around the clock so you show up every week with the answer already in hand. We're starting with NFL and NBA, with NHL, MLB, AFL, Premier League, and more on the way. Our mission is simple: make the smartest manager in the league the one who still has time for a life.
              </p>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
              <JAIMascot size={92} state="smug"></JAIMascot>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {ABOUT_PRINCIPLES.map((p) => (
                <div key={p.t} style={{ background: 'rgba(10,24,56,0.55)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 14, padding: '18px 20px', backdropFilter: 'blur(8px)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 7 }}>
                    <span style={{ color: 'var(--accent)', display: 'flex' }}><LCheck size={15}></LCheck></span>
                    <span style={{ fontSize: 16, fontWeight: 820, fontStyle: 'italic', textTransform: 'uppercase', letterSpacing: '-0.02em', color: 'white' }}>{p.t}</span>
                  </div>
                  <p style={{ fontSize: 14.5, lineHeight: 1.55, color: 'rgba(255,255,255,0.6)', margin: 0, textWrap: 'pretty' }}>{p.d}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  )
}

/* ── FINALE (closing CTA) ────────────────────────────────────────────────── */
export function V2Finale({ onAuth }) {
  return (
    <section style={{ textAlign: 'center', padding: '160px 40px 130px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', left: '50%', bottom: '-40%', transform: 'translateX(-50%)', width: 900, height: 700, background: 'radial-gradient(ellipse, color-mix(in oklab, var(--accent) 16%, transparent), transparent 60%)', pointerEvents: 'none' }}></div>
      <Reveal>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 6 }}>
          <JAIMascot size={130} state="excited"></JAIMascot>
        </div>
      </Reveal>
      <Reveal delay={120}>
        <h2 style={{ fontSize: 'clamp(70px, 9vw, 128px)', fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase', letterSpacing: '-0.06em', lineHeight: 0.95, margin: '14px 0 22px', color: 'white', position: 'relative' }}>Your move.</h2>
      </Reveal>
      <Reveal delay={220}>
        <p style={{ fontSize: 17.5, color: 'rgba(255,255,255,0.55)', margin: '0 0 40px', position: 'relative' }}>Sync your league in a minute. Get your first graded call tonight.</p>
        <button type="button" onClick={() => onAuth('signup')} className="v2-cta" style={{ display: 'inline-flex', alignItems: 'center', gap: 11, border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, color-mix(in oklab, var(--accent) 80%, white), var(--accent))', color: 'white', borderRadius: 14, padding: '19px 42px', fontSize: 17.5, fontWeight: 800, letterSpacing: '-0.01em', whiteSpace: 'nowrap', boxShadow: '0 18px 50px color-mix(in oklab, var(--accent) 40%, transparent)', position: 'relative', fontFamily: FONTS.tight }}>
          Get started <LArrow size={16}></LArrow>
        </button>
        {/* On iPhone, JAI is a real app — and the App Store cannot be searched
            for it yet, so this link is how people find it. */}
        <div style={{ marginTop: 26, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <div style={{ fontFamily: FONTS.mono, fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', color: 'rgba(255,255,255,0.38)' }}>OR TAKE JAI WITH YOU</div>
          <AppStoreButton dark></AppStoreButton>
        </div>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', marginTop: 20, position: 'relative' }}>
          Already have an account? <button type="button" onClick={() => onAuth('login')} style={{ font: 'inherit', color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>Sign in</button>
        </div>
      </Reveal>
    </section>
  )
}
